import React, { useState, useEffect, useMemo } from 'react';
import { Input, Dropdown, Menu, message } from 'antd';
import {
  SearchOutlined,
  MoreOutlined,
  UnorderedListOutlined,
  CalendarOutlined,
  EditOutlined
} from '@ant-design/icons';
import { MasterConfigs } from '../../../interfaces/entity';
import MasterComponent from '../../../components/Forms';
import CustomCalendar from './CustomCalendar';
import AddInterviewModal from './AddInterviewModal';
import InterviewScheduleModal, { TimelineEvent } from './InterviewScheduleModal';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { getData } from '../../../actions/apiCall';
import { postData, putData } from '../../../actions/developer';
import axios from 'axios';
import { getAccess } from '../../../actions/auth';
import moment from 'moment';
import ProfileCard from '../../../components/ContactInfo/ProfileCard';
import SmallProfileCard from '../../../components/ContactInfo/SmallProfileCard';
import { constants } from '../../../constants/common';
import { formatPriceIndian } from '../../../constants/functions';
import Badge from '../../../components/Badge';
import { Typography } from 'antd';
const { Text } = Typography;

const InterviewScheduleTable: React.FC = () => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editInterviewData, setEditInterviewData] = useState<any>(null);
  const [isFeedbackModalVisible, setIsFeedbackModalVisible] = useState(false);
  const [feedbackInterviewData, setFeedbackInterviewData] = useState<any>(null);
  const [interviewLevels, setInterviewLevels] = useState<{ name: string; value: string }[]>([]);
  const [formInstance, setFormInstance] = useState<any>(null);
  const [correctDateValue, setCorrectDateValue] = useState<dayjs.Dayjs | null>(null);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);

  // Updated Dropdown Menu
  const viewMenu = (
    <Menu
      selectedKeys={[viewMode]}
      onClick={({ key }) => setViewMode(key as 'list' | 'calendar')}
      className="rounded-lg shadow-lg border border-gray-100"
    >
      <Menu.Item
        key="list"
        icon={<UnorderedListOutlined className={viewMode === 'list' ? "text-blue-500" : ""} />}
      >
        <span className={`${viewMode === 'list' ? "text-gray-700 font-medium" : "text-gray-600"}`}>
          List
        </span>
      </Menu.Item>
      <Menu.Item
        key="calendar"
        icon={<CalendarOutlined className={viewMode === 'calendar' ? "text-blue-500" : ""} />}
      >
        <span className={`${viewMode === 'calendar' ? "text-gray-700 font-medium" : "text-gray-600"}`}>
          Calendar
        </span>
      </Menu.Item>
    </Menu>
  );

  const handleEditClick = async (record: any) => {
    try {
      axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
      const response = await dispatch(getData(`/contract_interview/${record._id}`));

      if (response?.code === 200 && response?.data) {
        setEditInterviewData(response.data);
        setIsEditModalVisible(true);
      } else {
        message.error('Failed to load interview details');
      }
    } catch (error) {
      console.error('Error fetching interview details:', error);
      message.error('Failed to load interview details');
    }
  };

  const handleFeedbackClick = async (record: any) => {
    try {
      axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
      const response = await dispatch(getData(`/contract_interview/${record._id}`));

      if (response?.code === 200 && response?.data) {
        const interviewData = response.data;
        let interviewDate = dayjs();
        if (interviewData.interview_datetime) {
          const dateValue = interviewData.interview_datetime;
          // Handle date parsing - MongoDB stores dates in UTC but we want to display as local time
          // The backend stores dates treating the input as local time, so we need to extract
          // UTC components and use them as if they were local time components
          if (dateValue instanceof Date) {
            // If it's a Date object from MongoDB (stored in UTC)
            // Extract UTC components and create a local date to preserve the original time
            const utcYear = dateValue.getUTCFullYear();
            const utcMonth = dateValue.getUTCMonth();
            const utcDate = dateValue.getUTCDate();
            const utcHour = dateValue.getUTCHours();
            const utcMinute = dateValue.getUTCMinutes();
            const utcSecond = dateValue.getUTCSeconds();
            // Create a dayjs object using UTC components as local time
            interviewDate = dayjs().year(utcYear).month(utcMonth).date(utcDate)
              .hour(utcHour).minute(utcMinute).second(utcSecond).millisecond(0);
          } else if (typeof dateValue === 'string') {
            // Parse the string first to get a date object
            const tempDate = new Date(dateValue);
            if (isNaN(tempDate.getTime())) {
              // If parsing fails, try dayjs directly
              interviewDate = dayjs(dateValue);
            } else {
              // Extract UTC components and use them as local time
              // This preserves the original time that was entered
              const utcYear = tempDate.getUTCFullYear();
              const utcMonth = tempDate.getUTCMonth();
              const utcDate = tempDate.getUTCDate();
              const utcHour = tempDate.getUTCHours();
              const utcMinute = tempDate.getUTCMinutes();
              const utcSecond = tempDate.getUTCSeconds();
              // Create a dayjs object using UTC components as local time
              interviewDate = dayjs().year(utcYear).month(utcMonth).date(utcDate)
                .hour(utcHour).minute(utcMinute).second(utcSecond).millisecond(0);
            }
          } else {
            interviewDate = dayjs(dateValue);
          }
        }

        // Calculate end time from duration
        const duration = interviewData.duration || 60;
        const endTime = interviewDate.clone().add(duration, 'minute');

        const timelineEvent: TimelineEvent = {
          id: interviewData._id,
          interviewId: interviewData._id,
          time: interviewDate.format('hh:mm A'),
          duration: `${duration}min`,
          title: interviewData.interviewTitle || 'Interview',
          status: interviewData.interviewStatus?.toLowerCase() || 'upcoming',
          candidate: {
            name: interviewData.developer
              ? `${interviewData.developer.firstName || ''} ${interviewData.developer.lastName || ''}`.trim()
              : 'N/A',
            email: interviewData.developer?.email || '',
            avatar: interviewData.developer?.avatar ? `${process.env.FILE_URL}${interviewData.developer.avatar}` : undefined,
            _id: interviewData.developer?._id
          },
          interviewer: {
            name: interviewData.interviewOwner
              ? `${interviewData.interviewOwner.firstName || ''} ${interviewData.interviewOwner.lastName || ''}`.trim()
              : 'N/A',
            email: interviewData.interviewOwner?.workEmail || '',
            avatar: interviewData.interviewOwner?.avatar ? `${process.env.FILE_URL}${interviewData.interviewOwner.avatar}` : undefined,
            _id: interviewData.interviewOwner?._id
          },
          role: interviewData.developer?.role || (typeof interviewData.developer?.jobTitle === 'object' ? interviewData.developer.jobTitle?.name : interviewData.developer?.jobTitle) || interviewData.job_description || 'N/A',
          dateStr: interviewDate.format('DD MMMM YYYY'),
          meetingLink: interviewData.interview_link || '',
          interviewLevel: interviewData.interview_type || interviewData.interviewTitle || '',
          developerId: interviewData.developer?._id,
          startTime: interviewDate.format('hh:mm A'),
          endTime: endTime.format('hh:mm A')
        };

        setFeedbackInterviewData(timelineEvent);
        setIsFeedbackModalVisible(true);
      } else {
        message.error('Failed to load interview details');
      }
    } catch (error) {
      console.error('Error fetching interview details:', error);
      message.error('Failed to load interview details');
    }
  };

  const handleRefresh = () => {
    // Trigger table refresh without page reload
    dispatch({
      type: 'REFRESH_TABLE',
      refreshTable: true
    });
  };

  const config: MasterConfigs = useMemo(() => ({
    api: () => '/contract_interview',
    isActionTab: true,
    getFormColumns: () => [
      {
        name: "interviewOwner",
        type: "select",
        label: "Interview Owner",
        isCombo: true,
        required: [{ required: true, message: "Interview Owner is required" }],
      },
      {
        name: "deal",
        type: "select",
        label: "Deal",
        isCombo: true,
        required: [{ required: true, message: "Deal is required" }],
        selectProps: {
          onChange: (value: any, option: any) => {
            // Extract deal ID from value (could be object or string)
            const dealId = value && typeof value === 'object' ? value._id : value;
            setSelectedDealId(dealId);
            // Clear developer field when deal changes
            if (formInstance) {
              formInstance.setFieldsValue({ developer: undefined });
            }
          }
        }
      },
      {
        name: "developer",
        type: "select",
        label: "Developer",
        isCombo: true,
        dependent: () => 'deal',
        required: [{ required: true, message: "Developer is required" }],
      },
      {
        label: "Interview Title",
        name: "interviewTitle",
        type: "text",
        span: 16,
        required: [{ required: true, message: "Interview Title is required" }],
      },
      {
        label: "Interview Type",
        name: "interview_type",
        type: "select",
        children: interviewLevels.length > 0 ? interviewLevels : [
          { name: "Screening", value: "Screening" },
          { name: 'Coding', value: 'Coding' },
          { name: "Technical Screening", value: "Technical" },
          { name: 'Level 1', value: 'Level 1' },
          { name: 'Level 2', value: 'Level 2' },
          { name: 'Level 3', value: 'Level 3' },
          { name: "HR", value: "HR" },
          { name: "Manager", value: "Manager" },
          { name: "Final", value: "Final" },
        ],
        required: [{ required: true, message: "Interview Round is required" }],
      },
      {
        label: "Interview Date & Time",
        name: "interview_datetime",
        type: "date",
        dateProps: { showTime: true, format: 'DD-MM-YYYY HH:mm A', showNow: true, showSecond: false },
        span: 8,
        required: [{ required: true, message: "Interview Date & Time is required" }],
      },
      {
        label: 'Duration',
        name: 'duration',
        type: 'text',
        inputProps: {
          addonAfter: 'Min'
        }
      },
      {
        label: "Interview Link",
        name: "interview_link",
        type: "text",
        rules: [{ required: true, message: 'Interview Link is required' }]
      },
      {
        label: "Job Description",
        name: "job_description",
        type: "textarea",
        span: 16
      },
      {
        label: "Instructions",
        name: "instructions",
        type: "textarea",
        span: 16
      }
    ],
    getColumns: () => [
      {
        title: 'Developer',
        dataIndex: "developer",
        key: "developer",
        width: 200,
        fixed: 'left',
        render: (text, record) => {
          const vendorPartnerName = text?.vendorPartner?.vendorName ||
            (text?.vendorPartner && typeof text.vendorPartner === 'object' ? text.vendorPartner.vendorName : null);

          const subTitleParts = [];
          if (vendorPartnerName) {
            subTitleParts.push(`Vendor: ${vendorPartnerName}`);
          }

          return <SmallProfileCard
            name={constants.GET_FULL_NAME(text)}
            avatar={text?.avatar}
            initials={constants.GET_FULL_NAME(text)?.charAt(0)}
            subTitle={subTitleParts.length > 0 ? subTitleParts.join(' • ') : undefined}
          />
        }
      },
      {
        title: 'Interview Status',
        dataIndex: "interviewStatus",
        key: "interviewStatus",
        width: 200,
        render: (text) => {
          // ['Selected', 'Rejected', 'Inprogress', 'Response awaiting']
          let color;
          if (text == 'Selected') {
            color = 'green'
          } else if (text == 'Rejected') {
            color = 'red'
          } else if (text == 'Inprogress') {
            color = 'yellow'
          } else if (text == 'Response awaiting') {
            color = 'orange'
          }

          return <Badge color={color} text={text} />
        }
      },
      {
        title: 'Deal',
        dataIndex: "deal",
        key: "deal",
        width: 200,
        render: (text) => text?.dealName
      },
      {
        title: 'Amount',
        dataIndex: "deal",
        key: "amount",
        width: 150,
        render: (text, record) => {
          // text is the deal object when dataIndex is "deal"
          // Use the same pattern as Account Name column which uses record?.deal
          const deal = text && typeof text === 'object' ? text : record?.deal;
          const amount = deal?.amount;
          const billingType = deal?.billingType;

          if (amount !== null && amount !== undefined && amount !== '') {
            // Format amount using currency formatter
            const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
            const formattedAmount = formatPriceIndian(numericAmount);
            // Add H for Hourly or M for Monthly based on billingType
            const billingSuffix = billingType === 'Hourly' ? ' H' : billingType === 'Monthly' ? ' M' : '';
            return `INR ${formattedAmount} /${billingSuffix}`;
          }
          return 'N/A';
        }
      },
      {
        title: 'Account Name',
        dataIndex: "accountName",
        key: "accountName",
        width: 200,
        render: (text, record) => record?.deal?.business?.businessName
      },
      {
        title: 'Interview Title',
        width: 200,
        dataIndex: "interviewTitle",
        key: "interviewTitle"
      },
      {
        title: 'Interview Type',
        width: 200,
        dataIndex: "interview_type",
        key: "interview_type"
      },
      {
        title: 'Interview Link',
        width: 200,
        dataIndex: "interview_link",
        key: "interview_link",
        ellipsis: {
          showTitle: true,
        },
        render: (text, record) => {
          const linkValue = text || record?.interview_link || '';
          if (!linkValue || linkValue.trim() === '') {
            return <span style={{ color: '#999' }}>N/A</span>;
          }
          const isUrl = linkValue.startsWith('http://') || linkValue.startsWith('https://');
          return (
            <Text copyable={{ text: linkValue }} style={{ display: 'block' }}>
              {isUrl ? (
                <a
                  href={linkValue}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ wordBreak: 'break-all' }}
                >
                  {linkValue}
                </a>
              ) : (
                <span style={{ wordBreak: 'break-all' }}>{linkValue}</span>
              )}
            </Text>
          );
        }
      },
      {
        title: 'Interview Date & Time',
        dataIndex: "interview_datetime",
        width: 200,
        key: "interview_datetime",
        fixed: 'right',
        render: (text) => {
          if (!text) return 'N/A';

          try {
            let date;
            if (typeof text === 'string') {
              date = moment.utc(text).utcOffset('+05:30');
            } else if (text instanceof Date) {
              date = moment.utc(text).utcOffset('+05:30');
            } else {
              date = moment.utc(text).utcOffset('+05:30');
            }

            if (!date.isValid()) {
              console.warn('Invalid date format:', text);
              return 'Invalid Date';
            }

            return date.format('DD-MM-YYYY hh:mm A');
          } catch (error) {
            console.error('Error formatting date:', error, text);
            return 'Invalid Date';
          }
        }
      },
      {
        title: 'Duration',
        dataIndex: 'duration',
        key: "duration",
        fixed: 'right',
        width: 100,
        render: (text) => text + ' Min'
      },
      {
        title: 'Interview Owner',
        dataIndex: "interviewOwner",
        width: 150,
        fixed: 'right',
        key: "interviewOwner",
        render: (text) => <SmallProfileCard
          name={constants.GET_FULL_NAME(text)}
          avatar={text?.avatar}
          initials={constants.GET_FULL_NAME(text)?.charAt(0)}
        />
      },
      {
        title: 'Feedback',
        dataIndex: 'interviewStatus',
        key: 'feedback',
        width: 100,
        fixed: 'right',
        align: 'center',
        render: (status, record) => (
          <span
            className="text-blue-500 cursor-pointer font-semibold hover:underline"
            onClick={() => handleFeedbackClick(record)}
          >
            {status === 'Selected' || status === 'Rejected' ? 'View' : 'Add'}
          </span>
        )
      },
    ],
    getFilterColumns: () => {
      const filterColumns: any[] = [
        {
          label: 'Developer',
          name: 'developer',
          type: 'select',
          isCombo: true,
          span: 6,
          selectProps: {
            allowClear: true,
            placeholder: 'Select Developer',
            showSearch: true,
          },
        },
        {
          label: 'Interview Owner',
          name: 'interviewOwner',
          type: 'select',
          isCombo: true,
          span: 6,
          selectProps: {
            allowClear: true,
            placeholder: 'Select Interview Owner',
            showSearch: true,
          },
        },
        {
          label: 'Start Date',
          name: 'startDate',
          type: 'date',
          span: 6,
          dateProps: {
            format: 'DD-MM-YYYY',
            allowClear: true,
            placeholder: 'Select Start Date',
          },
        },
        {
          label: 'End Date',
          name: 'endDate',
          type: 'date',
          span: 6,
          dateProps: {
            format: 'DD-MM-YYYY',
            allowClear: true,
            placeholder: 'Select End Date',
          },
        },
      ];
      return filterColumns;
    },
    actions: [
      {
        actionType: 'filter',
        name: 'Filter',
      },
      {
        name: "Schedule Interview", actionType: 'Add'
      },
      {
        name: "",
        actionType: 'Custom',
        render: () => {
          return (
            <Dropdown overlay={viewMenu} trigger={['click']} placement="bottomRight">
              <div className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                {viewMode === 'list' ? (
                  <UnorderedListOutlined className="text-blue-500 text-lg" />
                ) : (
                  <CalendarOutlined className="text-blue-500 text-lg" />
                )}
              </div>
            </Dropdown>
          );
        }
      }],
    combo: [
      {
        api: '/users',
        name: 'interviewOwner',
        idKey: '_id',
        render: (text) => <SmallProfileCard
          name={constants.GET_FULL_NAME(text)}
          avatar={text?.avatar}
          initials={constants.GET_FULL_NAME(text)?.charAt(0)}
        />
      },
      {
        api: '/deals',
        name: 'deal',
        idKey: '_id',
        render: (e) => e.dealName
      },
      {
        api: '/bench-developers',
        name: 'developer',
        idKey: '_id',
        render: (e) => [e.firstName, e.lastName].filter(Boolean).join(' '),
        rerender: selectedDealId ? [selectedDealId] : []
      },
    ],
    setForm: (form: any) => {
      setFormInstance(form);
    },
    onDataLoad: (data: any) => {
      // Transform interview_datetime when loading data for editing
      // This ensures the date picker shows the correct date
      // Note: transformData converts ISO strings to dayjs, but it does timezone conversion
      // We need to override it with the correct local time interpretation
      if (data?.interview_datetime) {
        const dateValue = data.interview_datetime;
        let interviewDate = null;


        // Handle date parsing - MongoDB stores dates in UTC but we want to display as local time
        // The original data from API might be a Date object or ISO string
        if (dateValue instanceof Date) {
          // If it's a Date object from MongoDB (stored in UTC)
          // Extract UTC components and create a local date to preserve the original time
          const utcYear = dateValue.getUTCFullYear();
          const utcMonth = dateValue.getUTCMonth();
          const utcDate = dateValue.getUTCDate();
          const utcHour = dateValue.getUTCHours();
          const utcMinute = dateValue.getUTCMinutes();
          const utcSecond = dateValue.getUTCSeconds();
          // Create a dayjs object using UTC components as local time
          interviewDate = dayjs().year(utcYear).month(utcMonth).date(utcDate)
            .hour(utcHour).minute(utcMinute).second(utcSecond).millisecond(0);
        } else if (typeof dateValue === 'string') {
          // Parse the string first to get a date object
          // If it's an ISO string with 'Z' or timezone, we need to extract UTC components
          const tempDate = new Date(dateValue);
          if (!isNaN(tempDate.getTime())) {
            // Extract UTC components and use them as local time
            const utcYear = tempDate.getUTCFullYear();
            const utcMonth = tempDate.getUTCMonth();
            const utcDate = tempDate.getUTCDate();
            const utcHour = tempDate.getUTCHours();
            const utcMinute = tempDate.getUTCMinutes();
            const utcSecond = tempDate.getUTCSeconds();
            // Create a dayjs object using UTC components as local time
            interviewDate = dayjs().year(utcYear).month(utcMonth).date(utcDate)
              .hour(utcHour).minute(utcMinute).second(utcSecond).millisecond(0);
          }
        }

        // Store the correct date value for the useEffect to use
        setCorrectDateValue(interviewDate);

        // Update the form field with the transformed date
        // Use a more aggressive approach to ensure it overrides transformData's conversion
        if (interviewDate && interviewDate.isValid()) {
          // Function to set the date value - will retry if form instance not ready
          const setDateValue = (retryCount = 0) => {
            if (formInstance) {
              try {
                // Use setFieldsValue to update the field
                formInstance.setFieldsValue({
                  interview_datetime: interviewDate
                });

                // Also try setFieldValue as a direct approach if available
                if (formInstance.setFieldValue) {
                  formInstance.setFieldValue('interview_datetime', interviewDate);
                }

                const currentValue = formInstance.getFieldValue('interview_datetime');
                if (currentValue) {
                  const diffMinutes = Math.abs(currentValue.diff(interviewDate, 'minute'));
                  // If the value is still incorrect and we haven't exceeded max retries, try again
                  if (diffMinutes > 1 && retryCount < 30) {
                    setTimeout(() => setDateValue(retryCount + 1), 50);
                  }
                }
              } catch (error) {
                console.error('Error setting form field:', error);
                if (retryCount < 30) {
                  setTimeout(() => setDateValue(retryCount + 1), 50);
                }
              }
            } else if (retryCount < 30) {
              // Retry if form instance not ready yet
              setTimeout(() => setDateValue(retryCount + 1), 50);
            }
          };

          // Set immediately - use multiple approaches to ensure it works
          setDateValue();

          // Set using microtask (runs before setTimeout)
          if (typeof queueMicrotask !== 'undefined') {
            queueMicrotask(() => setDateValue());
          } else {
            Promise.resolve().then(() => setDateValue());
          }

          // Set with setTimeout as backup at multiple intervals
          setTimeout(() => setDateValue(), 0);
          setTimeout(() => setDateValue(), 50);
          setTimeout(() => setDateValue(), 100);
          setTimeout(() => setDateValue(), 200);
          setTimeout(() => setDateValue(), 300);

          // Verify and correct after a delay
          setTimeout(() => {
            if (formInstance) {
              try {
                const currentValue = formInstance.getFieldValue('interview_datetime');
                if (currentValue) {
                  const diffMinutes = Math.abs(currentValue.diff(interviewDate, 'minute'));
                  console.log('Verifying date - Expected:', interviewDate.format('DD-MM-YYYY HH:mm A'),
                    'Actual:', currentValue.format('DD-MM-YYYY HH:mm A'),
                    'Diff (minutes):', diffMinutes);
                  // If the difference is more than 1 minute, it was incorrectly converted
                  if (diffMinutes > 1) {
                    console.log('Date was incorrectly converted, correcting...');
                    formInstance.setFieldsValue({
                      interview_datetime: interviewDate
                    });
                    if (formInstance.setFieldValue) {
                      formInstance.setFieldValue('interview_datetime', interviewDate);
                    }
                  }
                } else {
                  // If no value, set it
                  console.log('No date value found, setting it...');
                  formInstance.setFieldsValue({
                    interview_datetime: interviewDate
                  });
                }
              } catch (error) {
                console.error('Error verifying form field:', error);
              }
            }
          }, 200);
        } else {
          console.warn('Cannot set date - formInstance:', !!formInstance, 'interviewDate valid:', interviewDate?.isValid());
        }
      }
    },
    onSubmit: async (id: any, values: any) => {
      try {
        // Format interview_datetime - preserve local time without timezone conversion
        let interview_datetime = null;
        if (values.interview_datetime) {
          const dt = values.interview_datetime;
          // Check if it's a dayjs object
          if (dayjs.isDayjs(dt)) {
            // Create ISO string in local timezone format (without Z suffix)
            // Format: YYYY-MM-DDTHH:mm:ss (local time, backend will interpret correctly)
            const year = dt.year();
            const month = String(dt.month() + 1).padStart(2, '0');
            const day = String(dt.date()).padStart(2, '0');
            const hour = String(dt.hour()).padStart(2, '0');
            const minute = String(dt.minute()).padStart(2, '0');
            const second = String(dt.second()).padStart(2, '0');
            // Send without timezone indicator - backend should interpret as local/server time
            interview_datetime = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
          } else if (dt instanceof Date) {
            // If it's a Date object, format it similarly
            const year = dt.getFullYear();
            const month = String(dt.getMonth() + 1).padStart(2, '0');
            const day = String(dt.getDate()).padStart(2, '0');
            const hour = String(dt.getHours()).padStart(2, '0');
            const minute = String(dt.getMinutes()).padStart(2, '0');
            const second = String(dt.getSeconds()).padStart(2, '0');
            interview_datetime = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
          } else {
            // If it's already a string, use it as is
            interview_datetime = dt;
          }
        }

        const payload = {
          ...values,
          interview_datetime: interview_datetime,
          duration: values.duration ? parseInt(values.duration) : null,
          ...(id ? {} : { interviewStatus: 'Inprogress', status: 'active' })
        };

        axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());

        let response;
        if (id) {
          response = await dispatch(putData(`/contract_interview/${id}`, payload));
        } else {
          response = await dispatch(postData('/contract_interview', payload));
        }

        if (response?.code === 200 || response?._id) {
          message.success(id ? 'Interview updated successfully!' : 'Interview scheduled successfully!');
          handleRefresh();
          return { code: 200, data: response };
        } else {
          message.error(response?.message || `Failed to ${id ? 'update' : 'schedule'} interview`);
          return { code: 301, message: response?.message || 'Failed to process interview' };
        }
      } catch (error: any) {
        console.error(`Error ${id ? 'updating' : 'creating'} interview:`, error);
        // Check if it's a conflict error (409 status code)
        if (error?.response?.status === 409 || error?.code === 409) {
          message.error(error?.response?.data?.message || error?.message || 'Interview scheduling conflict: Developer already has an interview at this time.');
        } else {
          message.error(error?.response?.data?.message || error?.message || `Failed to ${id ? 'update' : 'schedule'} interview`);
        }
        return { code: 301, message: error?.message || 'Failed to process interview' };
      }
    }
  } as MasterConfigs), [interviewLevels, selectedDealId]);

  return (
    <div className="rounded-xl pb-[50px]">
      {/* Logic to switch between Table and Calendar */}
      {viewMode === 'list' ? (
        <>
          {/* <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-gray-800">Interview Schedule</h3>
            <div className="flex items-center gap-3">
              <Dropdown overlay={viewMenu} trigger={['click']} placement="bottomRight">
                <div className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                  {viewMode === 'list' ? (
                    <UnorderedListOutlined className="text-blue-500 text-lg" />
                  ) : (
                    <CalendarOutlined className="text-blue-500 text-lg" />
                  )}
                </div>
              </Dropdown>
            </div>
          </div> */}
          <MasterComponent config={config} />
          <AddInterviewModal
            visible={isEditModalVisible}
            onClose={() => {
              setIsEditModalVisible(false);
              setEditInterviewData(null);
            }}
            onSuccess={handleRefresh}
            editData={editInterviewData}
            isEditMode={true}
          />
          <InterviewScheduleModal
            visible={isFeedbackModalVisible}
            onClose={() => {
              setIsFeedbackModalVisible(false);
              setFeedbackInterviewData(null);
            }}
            event={feedbackInterviewData}
            onFeedbackAdded={handleRefresh}
          />
        </>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-bold text-gray-800">Interview Calendar</h3>
            <div className="flex">
              <Input
                size="middle"
                placeholder="search"
                prefix={<SearchOutlined className="text-gray-400" />}
                className="w-64 rounded-lg border-gray-200 bg-[#F9FAFB] hover:bg-white focus:bg-white transition-all mr-2"
              />
              <Dropdown overlay={viewMenu} trigger={['click']} placement="bottomRight">
                <div className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-lg cursor-pointer bg-white">
                  <CalendarOutlined className="text-blue-500 text-lg" />
                </div>
              </Dropdown>
            </div>
          </div>
          <div className="min-h-[400px] border border-dashed border-gray-200 rounded-lg flex items-center justify-center">
            <CustomCalendar />
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewScheduleTable;